// src/theme.ts
import { createTheme } from "@mui/material/styles";
import type { DualPalette, DualColor } from "./theme/types";
import { pick, type ColorMode } from "./theme/types";

// ----- IMPORT THE MODULAR THEME PARTS -----
import { background } from "./theme/base/background";
import { text } from "./theme/base/text";
import { primary } from "./theme/base/primary";
import { secondary } from "./theme/base/secondary";
import { neutral } from "./theme/base/neutral";

import { appBar } from "./theme/ui/appBar";
import { sidebars } from "./theme/ui/sidebars";
import { drawers } from "./theme/ui/drawers";
import { footer } from "./theme/ui/footer";

import { community } from "./theme/modules/community";
import { campaigns } from "./theme/modules/campaigns";
import { interactions } from "./theme/modules/interactions";
import { accounts } from "./theme/modules/accounts";
import { publicModule } from "./theme/modules/public";

import { nodeHeader } from "./theme/nodes/header";
import { nodeBackground } from "./theme/nodes/backgrounds";
import { nodeText } from "./theme/nodes/text";
import { nodeOutline } from "./theme/nodes/outline";
import { nodeFooter } from "./theme/nodes/footer";

import { edgePrimary } from "./theme/edges/primary";

import { system } from "./theme/functional/system";
import { utility } from "./theme/utility/utilities";

// -----------------------------------------------------------
// MUI THEME EXTENSION
// -----------------------------------------------------------
declare module "@mui/material/styles" {
  interface Theme {
    custom: {
      base: {
        background: DualPalette;
        text: DualPalette;
        primary: DualColor;
        secondary: DualColor;
        neutral: typeof neutral;
      };
      ui: {
        appBar: typeof appBar;
        sidebars: typeof sidebars;
        drawers: typeof drawers;
        footer: typeof footer;
      };
      modules: {
        community: typeof community;
        campaigns: typeof campaigns;
        interactions: typeof interactions;
        accounts: typeof accounts;
        publicModule: typeof publicModule;
      };
      nodes: {
        header: typeof nodeHeader;
        background: typeof nodeBackground;
        text: typeof nodeText;
        outline: typeof nodeOutline;
        footer: typeof nodeFooter;
      };
      edges: {
        primary: typeof edgePrimary;
      };
      functional: typeof system;
      utility: typeof utility;
    };
  }

  interface ThemeOptions {
    custom?: Theme["custom"];
  }
}

// -----------------------------------------------------------
// CREATE THEME (NOW TAKES MODE)
// -----------------------------------------------------------
export const createAppTheme = (mode: ColorMode) =>
  createTheme({
    palette: {
      mode, // important: lets MUI components behave correctly
      primary: { main: pick(primary, mode) },
      secondary: { main: pick(secondary, mode) },

      background: {
        default: pick(background.default, mode),
        paper: pick(background.paper, mode),
      },

      text: {
        // your 'text.primary' seems dual already
        primary: pick(text.primary, mode),
        secondary: pick(text.secondary, mode),
      },

      divider: mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
    },

    // keep your custom object exactly as-is (it still contains both modes)
    custom: {
      base: {
        background,
        text,
        primary,
        secondary,
        neutral,
      },
      ui: {
        appBar,
        sidebars,
        drawers,
        footer,
      },
      modules: {
        community,
        campaigns,
        interactions,
        accounts,
        publicModule,
      },
      nodes: {
        header: nodeHeader,
        background: nodeBackground,
        text: nodeText,
        outline: nodeOutline,
        footer: nodeFooter,
      },
      edges: {
        primary: edgePrimary,
      },
      functional: system,
      utility,
    },

    typography: {
      fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
    },
  });

// -----------------------------------------------------------
// DEFAULT THEME EXPORT (pick one as global default)
// -----------------------------------------------------------
const theme = createAppTheme("dark");
export default theme;
